/**
 * Import v2 메인 모달
 *
 * 단일 모달 안에서 phase/step 상태로 업로드 → 매핑 → 검증 → 완료 화면을 전환한다.
 */
import React, { useReducer, useCallback, useEffect, useRef, useState } from 'react'
import { Modal } from 'antd'
import classNames from 'classnames'
import history from 'history'

import { confirmBase, errorBase, PasswordInput } from 'utils/ui'
import { getTempEnvParam } from 'env'
import { EMAIL_REGEX } from 'dUtils'
import { isKR, isJP } from 'conf'
import { parseCsvRaw } from 'utils/importExport/externalImport'
import { readCsvText } from 'utils/importExport/utils/csv'
import {
  executeImportRecords,
  checkImportQuota,
  fetchDefaultTemplateId,
  fetchWebLoginTemplateId,
  fetchTemplateSchema,
  fetchTypeList,
  handleDarkWebCheckAfterImport,
  runDarkWebCheckWithAnimation,
  showDarkWebModal,
  isDarkWebActive,
  reloadMenu,
  IMPORT_ERROR,
  decryptRecords,
  unwrapExportData,
  validateImportRecord,
  validateTemplateId,
  toCustomFields,
  showImportFailedModal,
} from 'utils/importExport/bulkImport'
import { makeProgressContent, computeSHA256Hex } from 'utils/importExport/bulkExport'
import parseSafePasswordFile from 'utils/importExport/importers/safePasswordImporter'

import parseGoogleAuthUris from 'utils/importExport/importers/googleAuthImporter'
import { fetchExistingTotpFingerprints } from 'utils/importExport/totpDuplicateCheck'
import { totpFingerprint } from 'services/totp/duplicateRegistry'
import { featureFlags } from 'config/feature-flags'
import { isTotpCreateAllowed } from 'utils/plan'
// 복호화 비밀번호 팝업은 bulkImport 의 decrypt 팝업과 같은 시각 규격을 쓴다 (공용 시트 재사용)
import decryptStyles from 'utils/importExport/style.module.scss'
import importReducer, { initialState, PRESETS, TITLE_FIELD } from './importReducer'
import { validateRecords } from './importValidator'
import { fetchTemplateCatalog, findDefaultTemplateId } from './templateCatalog'
import {
  buildHeaderColumnMap,
  matchTemplateByHeaders,
  findDuplicateHeaders,
  dropTrailingEmptyRows,
  resolveImportTemplateId,
} from './templateForm'
import {
  validateCsvHeaders,
  isGoogleCsvHeaders,
  createMappingsFromSchema,
  GOOGLE_COLUMN_MAP,
  convertMappingToRecords,
  applyFixes,
  normalizeUrls,
  downloadExcludedCsv,
  getMappingBlockReason,
  getStepIndicator,
  tryNormalizeTotp,
} from './importUtils'

import ImportUploadStep from './ImportUploadStep'
import ImportMappingStep from './ImportMappingStep'
import ImportValidationStep from './ImportValidationStep'
import ImportCompleteStep from './ImportCompleteStep'
import { MappingBlockNotice, StepIndicator } from './Guidance'
import exclamationBlackIcon from '@/assets/images/exclamation-circle-black-16.svg'
import styles from './style.module.scss'

// 프리셋별 템플릿 ID fetcher (OfficePassword는 조회 불필요)
const PRESET_TEMPLATE_FETCHERS = {
  google: fetchWebLoginTemplateId,
  custom: fetchDefaultTemplateId,
}

// CSV 가 UTF-8 이 아닐 때 읽어 볼 지역별 폴백 인코딩(spec D12). 해외(KR·JP 외)는 폴백이 없어 UTF-8 이 아니면
// 해석 불가로 차단한다(U7). 중첩 삼항식은 no-nested-ternary 에 걸려 함수로 둔다
const CSV_FALLBACK_ENCODING_BY_REGION = { kr: 'euc-kr', jp: 'shift_jis' }
const getCsvFallbackEncoding = () => {
  if (isKR()) return CSV_FALLBACK_ENCODING_BY_REGION.kr
  if (isJP()) return CSV_FALLBACK_ENCODING_BY_REGION.jp
  return null
}

const ImportV2Modal = ({ visible, onClose, t, messageApi, initialPresetId }) => {
  const [state, dispatch] = useReducer(importReducer, initialState)
  // v2.6: 완료 화면 "확인" 버튼 중복 클릭 방지 + 다크웹 체크 진행 중 disabled
  const [isFinalizing, setIsFinalizing] = useState(false)
  // v2.7: 템플릿 data_types (minLength 등 검증 규칙용)
  const [typeList, setTypeList] = useState([])
  // QR 이미지 인식 중 — 이때 [다음]을 누르면 중복 검사가 그 시점 목록으로 돌아 인식 중이던 QR 이 결과에서
  // 빠진다. 인식 결과를 버리는 대신 [다음]을 막아 사용자가 올린 QR 을 잃지 않게 한다
  const [isQrScanning, setIsQrScanning] = useState(false)
  // v2.12: 검증 step 진입 시 오류 0건이면 자동 import 실행. 중복 호출 방지용 ref.
  const autoImportTriggeredRef = useRef(false)
  // PR7: 기존 TOTP 지문 전수 복호 결과 캐시 — 같은 모달 세션에서 뒤로갔다 다시 Next 해도
  // 재복호하지 않는다(리뷰 D2). 모달 재열기 시 아래 effect 에서 초기화.
  const existingTotpRef = useRef(null)
  // 가져오기 작업(파싱·복호화·QR 확인)마다 붙이는 번호표 — 창을 닫으면 바뀌어 늦게 끝난 작업의 결과를 버린다.
  // 주 방어는 처리 중 닫기 차단(isParsing)이고 이것은 출구를 빠뜨렸을 때의 보험이다
  const parseGenerationRef = useRef(0)
  // 복호화 비밀번호 팝업(정적 Modal.confirm)은 자기 container 에 따로 렌더되므로(antd 5.21.6 es/modal/confirm.js:49·86)
  // 가져오기 창을 닫아도 남는다 — 닫을 때 직접 정리한다
  const decryptModalRef = useRef(null)
  // 템플릿 카탈로그 요청의 번호표 — 모달이 닫히면 바뀌어 늦게 온 응답이 새 세션을 덮어쓰지 못한다(spec D2)
  const catalogGenerationRef = useRef(0)

  const {
    phase,
    step,
    preset,
    fileName,
    file,
    parsedData,
    mappings,
    validationErrors,
    validRecords,
    importResult,
    isImporting,
    templateSchemas,
    scannedQrs,
    isParsing,
    templateCatalog,
    selectedTemplateId,
    templateDetection,
    mappingTemplateId,
  } = state

  const stepLabel = phase === 'steps' ? preset.steps[step] : null

  // Import 건수 계산 (v2.3: action='fixed' 기준)
  const fixedList = validationErrors.filter((e) => e.action === 'fixed')
  const exclList = validationErrors.filter((e) => e.action === 'exclude')
  // v2.3 § 16.4: 모든 항목이 수정(fixed)/제외(exclude) 완료 상태여야 Import 실행 가능
  const pendingCount = validationErrors.filter(
    (e) => e.action === null || e.action === 'editing',
  ).length
  const importCnt = validRecords.length + fixedList.length

  // CSV 컬럼 옵션 (매핑 스텝용)
  const csvColumns = parsedData && parsedData.headers ? parsedData.headers : []

  // 현재 프리셋의 템플릿 스키마 상태 조회
  const currentTemplateState = templateSchemas[preset.id]
  const isTemplatePreset = preset.id === 'template'
  const selectedTemplate = isTemplatePreset
    ? templateCatalog.items.find((item) => item.id === selectedTemplateId) || null
    : null
  // 저장될 템플릿 이름 — 열 연결 화면 위쪽에 한 줄로 보인다(spec D16). 실행은 선택이 아니라 매핑을 만든 템플릿으로 한다
  const savedAsTemplate = isTemplatePreset
    ? templateCatalog.items.find((item) => item.id === mappingTemplateId) || null
    : null
  // 템플릿 양식은 카탈로그가 로드되고 판별·선택이 끝나야 준비된 것이다(판별 중에는 selectedTemplate 이 null).
  // googleauth(PR7)는 매핑 스텝이 없어 스키마 불필요(템플릿 id 는 실행부에서 조회)
  const getIsTemplateReady = () => {
    if (isTemplatePreset) return templateCatalog.status === 'success' && !!selectedTemplate
    if (preset.id === 'officepassword' || preset.id === 'googleauth') return true
    return currentTemplateState?.status === 'success'
  }
  const isTemplateReady = getIsTemplateReady()

  // 프리셋 선택 시 템플릿 스키마 백그라운드 로드 (v2.3 § 15.3)
  const loadTemplateForPreset = useCallback(
    async (presetId) => {
      const fetcher = PRESET_TEMPLATE_FETCHERS[presetId]
      if (!fetcher) return // officepassword는 조회 불필요

      dispatch({ type: 'LOAD_TEMPLATE_START', payload: { presetId } })
      try {
        const templateId = await fetcher(messageApi, t)
        if (!templateId) throw new Error('TEMPLATE_NOT_FOUND')
        const schema = await fetchTemplateSchema(templateId)
        dispatch({ type: 'LOAD_TEMPLATE_SUCCESS', payload: { presetId, schema } })
      } catch (error) {
        console.error('Template schema load failed:', error)
        dispatch({
          type: 'LOAD_TEMPLATE_ERROR',
          payload: { presetId, error: error.message || 'UNKNOWN' },
        })
      }
    },
    [messageApi, t],
  )

  // 템플릿 양식 프리셋의 카탈로그(전역 기본 템플릿 목록·스키마) 로드. 응답이 닫힌 뒤·재요청 뒤에 오면 버린다(spec D2).
  // 조회는 기존 fetchTypeList 와 같은 safeboxClient 체인을 타므로 새로 생기는 부수효과는 없다
  const loadTemplateCatalog = useCallback(async () => {
    catalogGenerationRef.current += 1
    const generation = catalogGenerationRef.current
    dispatch({ type: 'LOAD_CATALOG_START' })
    try {
      const catalog = await fetchTemplateCatalog()
      if (generation !== catalogGenerationRef.current) return
      dispatch({ type: 'LOAD_CATALOG_SUCCESS', payload: catalog })
    } catch (error) {
      if (generation !== catalogGenerationRef.current) return
      console.error('Template catalog load failed:', error)
      dispatch({ type: 'LOAD_CATALOG_ERROR' })
    }
  }, [])

  useEffect(() => {
    if (!visible) return
    if (preset.id === 'officepassword' || preset.id === 'googleauth') return
    // 템플릿 양식은 프리셋별 스키마가 아니라 카탈로그를 쓴다 — error 면 사용자 재시도 버튼으로 다시 부른다
    if (isTemplatePreset) {
      if (templateCatalog.status === 'idle') loadTemplateCatalog()
      return
    }
    const current = templateSchemas[preset.id]
    // 이미 success이거나 loading이면 재호출 안 함. error면 사용자 재시도 버튼으로 재호출.
    if (current && current.status !== undefined && current.status !== 'idle') return
    loadTemplateForPreset(preset.id)
  }, [
    visible,
    preset.id,
    templateSchemas,
    loadTemplateForPreset,
    isTemplatePreset,
    templateCatalog.status,
    loadTemplateCatalog,
  ])

  // PR7: 모달 재열기 시 복호 캐시 초기화(새 세션 — 레코드가 바뀌었을 수 있음)
  useEffect(() => {
    if (visible) existingTotpRef.current = null
  }, [visible])

  // 모달을 닫으면 진행 중이던 카탈로그 요청의 결과를 버린다(spec D2 세션 경계)
  useEffect(() => {
    if (!visible) catalogGenerationRef.current += 1
  }, [visible])

  // 템플릿 양식: 파일을 고를 때마다 헤더로 템플릿을 판별해 선택한다(spec D6). 판별은 비동기라 cleanup 의
  // cancelled 로 늦게 끝난 판별이 이후 파일 선택을 덮어쓰지 못하게 한다(F7). 해석 불가(text null)도 여기서는
  // 기본 형식으로 내리기만 한다 — 차단은 '다음' 시점의 parseFileAndAdvance 가 한다
  useEffect(() => {
    if (!isTemplatePreset || !file || templateCatalog.status !== 'success') return undefined
    let cancelled = false
    const selectFallback = () =>
      dispatch({
        type: 'SELECT_TEMPLATE',
        payload: { id: findDefaultTemplateId(templateCatalog.items), detection: 'fallback' },
      })
    readCsvText(file, getCsvFallbackEncoding())
      .then(({ text }) => {
        if (cancelled) return
        const matchedId =
          text == null
            ? null
            : matchTemplateByHeaders(templateCatalog.items, parseCsvRaw(text).headers)
        if (matchedId) {
          dispatch({ type: 'SELECT_TEMPLATE', payload: { id: matchedId, detection: 'matched' } })
        } else {
          selectFallback()
        }
      })
      .catch(() => {
        if (!cancelled) selectFallback()
      })
    return () => {
      cancelled = true
    }
  }, [isTemplatePreset, file, templateCatalog])

  // 외부에서 닫히는 경우의 보험 — handleClose 를 거치지 않아도 늦게 끝난 작업의 결과를 버리고, 따로 렌더되는
  // 비밀번호 팝업도 정리한다(closeAndReset 과 같은 두 가지). closeAndReset 이 먼저 ref 를 비우므로 거기서 온
  // 닫힘에는 아무 일도 하지 않는다
  useEffect(() => {
    if (!visible) {
      parseGenerationRef.current += 1
      decryptModalRef.current?.destroy()
      decryptModalRef.current = null
    }
  }, [visible])

  // PR7: 허브 탭 버튼 진입 — googleauth 프리셋 preselect
  useEffect(() => {
    if (!visible || !initialPresetId) return
    // flag OFF 에서 preselect 로 googleauth 가 새는 것 방지(리뷰 B3 — 이중 게이트).
    // totpGoogleImport 는 1차 TOTP 배포에서 import 만 늦추기 위한 하위 flag.
    if (
      initialPresetId === 'googleauth' &&
      !(isTotpCreateAllowed() && featureFlags.totpGoogleImport)
    )
      return
    const target = PRESETS.find((p) => p.id === initialPresetId)
    if (target && preset.id !== initialPresetId) {
      dispatch({ type: 'SET_PRESET', payload: target })
    }
    // preset.id 를 deps 에 넣으면 사용자가 다른 프리셋으로 바꿀 때마다 되돌아가므로 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, initialPresetId])

  // v2.7: 모달 진입 시 data_types 로드 (모든 프리셋 공통 사용)
  useEffect(() => {
    if (!visible) return undefined
    let cancelled = false
    fetchTypeList().then((list) => {
      if (!cancelled) setTypeList(list || [])
    })
    return () => {
      cancelled = true
    }
  }, [visible])

  // ── googleauth(PR7): 누적 QR → records + 전수 복호 중복 검사 ──

  const handleGoogleAuthNext = useCallback(async () => {
    if (isParsing || isQrScanning) return
    parseGenerationRef.current += 1
    const generation = parseGenerationRef.current
    const isStale = () => generation !== parseGenerationRef.current
    dispatch({ type: 'SET_PARSING', payload: true })
    try {
      const result = parseGoogleAuthUris(scannedQrs.map((q) => q.uri))
      if (result.type === 'error') {
        showImportFailedModal(t)
        return
      }
      if (result.type === 'empty') {
        showImportFailedModal(t, { customMessage: t('record.totp.import.error.noAccounts') })
        return
      }

      // 기존 TOTP 전수 복호(인터뷰 확정 B) — 완전한 중복 판정. 실패 건수는 고지용.
      // 같은 모달 세션 재진입(뒤로→다음)은 캐시 재사용(리뷰 D2 — RSA unwrap 반복 방지).
      let existing = existingTotpRef.current
      if (!existing) {
        const loading = messageApi.open({
          type: 'loading',
          content: t('record.totp.import.checkingExisting'),
          duration: 0,
        })
        try {
          existing = await fetchExistingTotpFingerprints()
        } catch (e) {
          // 목록 조회 실패 — 중복 미판정으로 진행(허브 배지가 사후 보완). 아래에서 명시 고지.
          existing = { fingerprints: new Set(), failedCount: -1 }
        } finally {
          loading()
        }
        // 조회 실패는 위 catch 가 삼키고 진행하므로 await 직후가 아니라 이 블록 뒤에서 본다
        if (isStale()) return
        existingTotpRef.current = existing
      }

      const validation = validateRecords(result.records, preset, [], typeList)

      // 중복 계정은 기본 제외(action:'exclude') — 사용자가 검증 화면에서 해제 가능.
      // 기존 레코드와의 중복 + 가져올 목록 내부 중복(같은 계정 이중 스캔)을 함께 판정.
      const duplicateErrors = []
      const duplicateRecords = new Set()
      const seenInBatch = new Set()
      result.records.forEach((record, idx) => {
        const fp = totpFingerprint(record.items[0]?.content)
        const isDup = fp != null && (existing.fingerprints.has(fp) || seenInBatch.has(fp))
        if (fp != null) seenInBatch.add(fp)
        if (isDup) {
          duplicateRecords.add(record)
          duplicateErrors.push({
            id: `dup-${idx}`,
            row: idx + 1,
            field: TITLE_FIELD,
            value: record.title,
            reason: 'duplicateTotp',
            messageKey: 'record.totp.import.duplicateExcluded',
            messageParams: {},
            uiType: 'text',
            minLength: null,
            action: 'exclude',
            fix: '',
            editError: null,
          })
        }
      })
      // 중복 행을 validRecords 에서 제거 — validRecords 는 원본 record 참조를 담으므로
      // (importValidator 실측) identity 로 매칭한다. 행번호 기반 필터는 유효성 오류로
      // 배열이 축약됐을 때 엉뚱한 레코드를 제거한다(리뷰 A2/B1 — 인덱스 공간 불일치).
      const validRecordsFiltered = validation.validRecords.filter(
        (record) => !duplicateRecords.has(record),
      )

      if (existing.failedCount === -1) {
        // 전면 실패(목록 조회 불가) — 중복 검사가 통째로 생략됐음을 명시 고지(리뷰 B2)
        messageApi.warning(t('record.totp.import.duplicateCheckSkipped'))
      } else if (existing.failedCount > 0) {
        messageApi.warning(
          t('record.totp.import.uncheckedExisting', { count: existing.failedCount }),
        )
      }

      dispatch({ type: 'SET_PARSED_DATA', payload: { parsedData: result.records } })
      dispatch({
        type: 'SET_VALIDATION_RESULT',
        payload: {
          errors: [...validation.errors, ...duplicateErrors],
          validRecords: validRecordsFiltered,
          totalRows: result.records.length,
        },
      })
      dispatch({ type: 'GO_NEXT' })
    } finally {
      if (!isStale()) dispatch({ type: 'SET_PARSING', payload: false })
    }
  }, [scannedQrs, preset, t, messageApi, typeList, isParsing, isQrScanning])

  // ── 파일 파싱 ──

  const parseFileAndAdvance = useCallback(async () => {
    if (!file) {
      dispatch({ type: 'GO_NEXT' })
      return
    }
    if (isParsing) return
    parseGenerationRef.current += 1
    const generation = parseGenerationRef.current
    const isStale = () => generation !== parseGenerationRef.current
    dispatch({ type: 'SET_PARSING', payload: true })

    // 바깥 finally 는 잠금 해제 전용 — 안쪽의 early return·catch 경로도 모두 여기를 거친다
    try {
      try {
        if (preset.id === 'officepassword') {
          // JSON 은 UTF-8 로만 읽는다. CSV 는 아래에서 인코딩 폴백(readCsvText)으로 따로 읽는다
          const text = await file.text()
          if (isStale()) return

          const result = parseSafePasswordFile(text)

          if (result.type === 'error') {
            showImportFailedModal(t)
            return
          }

          if (result.type === 'encrypted') {
            await handleEncryptedJson(result.data, t, messageApi, dispatch, {
              preset,
              typeList,
              isStale,
              decryptModalRef,
            })
            return
          }

          if (result.type === 'backup') {
            let records = unwrapExportData(result.data)
            if (!Array.isArray(records)) records = [records]
            dispatch({ type: 'SET_PARSED_DATA', payload: { parsedData: records } })

            // 유효성 검증 결과를 dispatch하고 validation 화면으로 이동 (에러 유무 무관)
            const validation = validateRecords(records, preset, [], typeList)
            dispatch({
              type: 'SET_VALIDATION_RESULT',
              payload: {
                errors: validation.errors,
                validRecords: validation.validRecords,
                totalRows: records.length,
              },
            })
            dispatch({ type: 'GO_NEXT' })
            return
          }

          showImportFailedModal(t)
          return
        }

        // CSV 파싱 (Google / Custom / 템플릿 양식) — 스키마(템플릿 양식은 카탈로그)가 로드되어 있어야 매핑 생성 가능.
        // 해석 불가 인코딩은 전 지역·전 CSV 프리셋에서 차단한다 — 깨진 글자가 조용히 저장되지 않게(spec D12, U7)
        const { text: csvText } = await readCsvText(file, getCsvFallbackEncoding())
        if (isStale()) return
        if (csvText == null) {
          showImportFailedModal(t, {
            customMessage: t('record.import.v2.error.unreadableEncoding'),
          })
          return
        }
        const parsedCsv = parseCsvRaw(csvText)
        // 템플릿 양식만 끝의 빈 줄을 뗀다(기존 프리셋 동작은 바꾸지 않는다)
        const parsed = isTemplatePreset
          ? { ...parsedCsv, rows: dropTrailingEmptyRows(parsedCsv.rows) }
          : parsedCsv
        if (!validateCsvHeaders(parsed.headers)) {
          showImportFailedModal(t)
          return
        }

        // v2.5: Google 프리셋은 헤더 검증. GOOGLE_COLUMN_MAP 매치 3/5 미만이면 Google CSV가 아님.
        if (preset.id === 'google' && !isGoogleCsvHeaders(parsed.headers)) {
          showImportFailedModal(t, { customMessage: t('record.import.v2.error.notGoogleCsv') })
          return
        }

        // 정규화한 헤더가 겹치면 값이 말없이 덮어써진다(parseCsvRaw) — 템플릿 양식만 차단한다(spec D15, U9)
        if (isTemplatePreset) {
          const duplicates = findDuplicateHeaders(parsed.headers)
          if (duplicates.length > 0) {
            showImportFailedModal(t, {
              customMessage: t('record.import.v2.error.duplicateHeaders', {
                columns: duplicates.join(', '),
              }),
            })
            return
          }
          // 헤더만 있는 파일은 저장할 행이 없다 — 매핑·검증 화면까지 보내지 않고 여기서 알린다
          if (parsed.rows.length === 0) {
            showImportFailedModal(t, { customMessage: t('record.import.v2.error.noDataRows') })
            return
          }
        }

        // 프리셋별 스키마·열 연결·data_types. 템플릿 양식은 카탈로그가 가진 선택 템플릿과 data_types 를 쓴다 —
        // 별도 fetchTypeList 의 지연·실패로 typeInfo 가 비는 창이 없다(spec D2)
        let schema = null
        let columnMap = null
        let mappingTypeList = typeList
        if (isTemplatePreset) {
          schema = selectedTemplate?.schema || null
          columnMap = schema ? buildHeaderColumnMap(schema, parsed.headers) : null
          mappingTypeList = templateCatalog.dataTypes
        } else {
          const schemaState = templateSchemas[preset.id]
          schema = schemaState?.status === 'success' ? schemaState.schema : null
          columnMap = preset.id === 'google' ? GOOGLE_COLUMN_MAP : null
        }
        if (!schema) {
          // 스키마 로드가 아직 안 끝났거나 실패한 경우 (버튼 비활성화로 일반 사용자는 여기 도달 안 함)
          showImportFailedModal(t)
          return
        }

        dispatch({ type: 'SET_PARSED_DATA', payload: { parsedData: parsed } })

        const mappingRows = createMappingsFromSchema(
          schema,
          parsed.headers,
          columnMap,
          mappingTypeList,
        )
        dispatch({ type: 'INIT_MAPPINGS', payload: mappingRows })
        // 실행은 선택이 아니라 매핑을 만든 템플릿으로 한다 — 이후 업로드 화면에서 선택이 바뀔 수 있다(spec D9)
        if (isTemplatePreset)
          dispatch({ type: 'SET_MAPPING_TEMPLATE', payload: selectedTemplateId })

        dispatch({ type: 'GO_NEXT' })
      } catch (error) {
        // 닫힌 창 위에 실패 창을 띄우지 않는다
        if (isStale()) return
        console.error('Import file parse error:', error)
        showImportFailedModal(t)
      }
    } finally {
      if (!isStale()) dispatch({ type: 'SET_PARSING', payload: false })
    }
  }, [
    file,
    preset,
    t,
    messageApi,
    templateSchemas,
    typeList,
    isParsing,
    isTemplatePreset,
    selectedTemplate,
    selectedTemplateId,
    templateCatalog,
  ])

  // ── 매핑 → 검증 전환 ──

  const handleMappingNext = useCallback(() => {
    if (!parsedData || !parsedData.rows) return

    const records = convertMappingToRecords(parsedData.rows, mappings, t)
    const validation = validateRecords(records, preset, mappings, typeList)

    // 변환된 records를 parsedData에 저장 (Import 실행 시 사용)
    dispatch({
      type: 'SET_PARSED_DATA',
      payload: { parsedData: { ...parsedData, convertedRecords: records } },
    })
    dispatch({
      type: 'SET_VALIDATION_RESULT',
      payload: {
        errors: validation.errors,
        validRecords: validation.validRecords,
        totalRows: records.length,
      },
    })
    dispatch({ type: 'GO_NEXT' })
  }, [parsedData, mappings, preset, t, typeList])

  // ── Import 실행 ──

  const handleImportExecute = useCallback(async () => {
    // v2.3: 저장 시점에 이미 재검증되어 있으므로 Import 실행 시 재검증 불필요.
    // pendingCount === 0 가드(다음 버튼 비활성화)로 이미 차단됨.
    dispatch({ type: 'SET_IMPORTING', payload: true })

    // records 준비
    const fixedErrors = validationErrors.filter((e) => e.action === 'fixed' && (e.fix || '').trim())
    const excludedRows = new Set(
      validationErrors.filter((e) => e.action === 'exclude').map((e) => e.row),
    )

    let allRecords
    if (preset.id === 'officepassword' || preset.id === 'googleauth') {
      allRecords = parsedData // records 배열 직접(매핑 스텝 없음)
    } else {
      allRecords = parsedData.convertedRecords || []
    }

    // 수정값 반영
    let recordsToImport = applyFixes(allRecords, fixedErrors)

    // 제외 행 제거 (row는 1-based)
    recordsToImport = recordsToImport.filter((_, idx) => !excludedRows.has(idx + 1))

    // validateImportRecord 체크 + URL 자동 보정 (프로토콜 없으면 https:// 추가)
    recordsToImport = recordsToImport.filter((r) => validateImportRecord(r))
    recordsToImport = normalizeUrls(recordsToImport)

    // PR10: totp bare secret URI 화의 단일 지점 — 제목/값 fix 가 모두 반영된 뒤라야
    // URI label(=r.title)이 stale 해지지 않는다(PR #1543 리뷰 major). 원본 otpauth URI 는
    // tryNormalizeTotp 가 파라미터 보존, 깨진 값은 검증 단계가 이미 수정/제외 처리.
    recordsToImport = recordsToImport.map((r) => ({
      ...r,
      items: (r.items || []).map((item) =>
        item.type === 'totp' ? { ...item, content: tryNormalizeTotp(item.content, r.title) } : item,
      ),
    }))

    if (recordsToImport.length === 0) {
      dispatch({ type: 'SET_IMPORTING', payload: false })
      errorBase({
        title: t('record.import.v2.error.title'),
        content: t('record.error.importNoRecords'),
      })
      return
    }

    // 템플릿 할당 — 템플릿 양식은 매핑을 만든 템플릿, 그 외는 프리셋별 기존 조회(spec D9)
    let templateId = resolveImportTemplateId({ presetId: preset.id, mappingTemplateId })
    if (!templateId) {
      const fetchTemplateId =
        preset.id === 'google' ? fetchWebLoginTemplateId : fetchDefaultTemplateId
      templateId = await fetchTemplateId(messageApi, t)
    }
    if (!templateId) {
      // /v1/resource-templates 조회 실패(예: 오프라인) — 빈 모달 대신 실패 결과로 완료 화면 이동
      dispatch({
        type: 'SET_IMPORT_RESULT',
        payload: {
          successCount: 0,
          failCount: recordsToImport.length,
          excludedCount: exclList.length,
          importedResourceIds: [],
          hasEmails: false,
        },
      })
      dispatch({ type: 'SET_IMPORTING', payload: false })
      dispatch({ type: 'GO_NEXT' })
      return
    }

    // OfficePassword JSON: 기존 template 검증 + fallback
    if (preset.id === 'officepassword') {
      const templateValidationCache = new Map()
      const checkTemplateValid = (tid) => {
        if (!templateValidationCache.has(tid)) {
          templateValidationCache.set(tid, validateTemplateId(tid))
        }
        return templateValidationCache.get(tid)
      }

      const defaultTemplateId = templateId
      await Promise.all(
        recordsToImport.map(async (record, i) => {
          const hasId = !!record.template?.id
          const isValid = hasId ? await checkTemplateValid(record.template.id) : false
          if (!hasId || !isValid) {
            recordsToImport[i] = toCustomFields(recordsToImport[i])
            recordsToImport[i].template = { id: defaultTemplateId }
          }
        }),
      )
    } else {
      recordsToImport.forEach((r) => {
        r.template = { id: templateId }
      })
    }

    // 할당량 체크
    const canImport = await checkImportQuota(recordsToImport.length, t)
    if (!canImport) {
      dispatch({ type: 'SET_IMPORTING', payload: false })
      return
    }

    // Progress 모달 + 실행
    const progressModal = Modal.confirm({
      title: t('record.result.importLoading'),
      icon: null,
      width: 480,
      content: makeProgressContent(0, recordsToImport.length, t),
      closable: false,
      keyboard: false,
      maskClosable: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      wrapClassName: classNames('om-popup-1'),
    })

    const result = await executeImportRecords(recordsToImport, (current, total) => {
      progressModal.update({ content: makeProgressContent(current, total, t) })
    })

    // 메뉴 리로드 — §9.2 결정에 따라 Import 직후 유지 (다크웹과 분리)
    reloadMenu()

    // v2.6: 다크웹 체크는 handleConfirm("확인" 버튼)으로 이동. Progress 모달만 정리.
    progressModal.destroy()

    // v2.9: 이메일 유무 사전 체크 — RecordEdit의 ZDW 조건과 동일하게 정렬
    // (어떤 item의 content에도 이메일 없는 경우 ZDW 조회 skip)
    const hasEmails = recordsToImport.some(
      (r) =>
        Array.isArray(r.items) &&
        r.items.some((it) => {
          const content = String(it.content || '')
          return content && EMAIL_REGEX.test(content)
        }),
    )

    // 결과 저장
    dispatch({
      type: 'SET_IMPORT_RESULT',
      payload: {
        successCount: result.successCount,
        failCount: result.failCount,
        excludedCount: exclList.length,
        importedResourceIds: result.importedResourceIds,
        hasEmails,
      },
    })
    dispatch({ type: 'SET_IMPORTING', payload: false })
    dispatch({ type: 'GO_NEXT' })
  }, [validationErrors, parsedData, preset, t, messageApi, exclList, mappingTemplateId])

  // v2.12: 검증 step 진입 시 오류 0건이면 검증 화면 건너뛰고 바로 import 자동 실행
  const shouldAutoImport =
    phase === 'steps' &&
    stepLabel === '유효성 검증' &&
    validationErrors.length === 0 &&
    validRecords.length > 0

  useEffect(() => {
    if (!shouldAutoImport) {
      autoImportTriggeredRef.current = false
      return
    }
    if (autoImportTriggeredRef.current) return
    autoImportTriggeredRef.current = true
    handleImportExecute()
  }, [shouldAutoImport, handleImportExecute])

  // ── 완료 화면 핸들러 ──

  // v2.6: 완료 화면 "확인" 버튼 — 다크웹 체크 + 모달 닫기 + 리스트 새로고침
  const handleConfirm = async () => {
    if (isFinalizing) return
    setIsFinalizing(true)

    try {
      const validIds = (importResult?.importedResourceIds || []).filter((id) => id != null)
      // v2.9: ZDW 조회 조건을 RecordEdit과 동일하게 정렬 — zdw_active + 이메일 실제 존재
      const shouldCheckZdw =
        isDarkWebActive() && validIds.length > 0 && importResult?.hasEmails === true
      if (shouldCheckZdw && validIds.length > 1) {
        const dwModal = showDarkWebModal(t, validIds.length)
        await runDarkWebCheckWithAnimation(validIds, (content) => dwModal.update({ content }), t)
        dwModal.destroy()
      } else if (shouldCheckZdw) {
        await handleDarkWebCheckAfterImport(importResult.importedResourceIds)
      }
    } finally {
      onClose()
      dispatch({ type: 'RESET' })
      setIsFinalizing(false)

      const tablePage = getTempEnvParam('curTablePage')
      if (tablePage && tablePage.reqData) {
        tablePage.reqData(null)
      } else {
        history.push('/records/all')
      }
    }
  }

  const handleDownloadExcluded = () => {
    const excluded = validationErrors.filter((e) => e.action === 'exclude')
    // officepassword/googleauth 는 parsedData 가 records 배열 자체(매핑 스텝 없음)
    const isDirectRecords = preset.id === 'officepassword' || preset.id === 'googleauth'
    const originalRows = isDirectRecords ? parsedData : parsedData?.rows || []
    const headers = !isDirectRecords ? parsedData?.headers : null
    downloadExcludedCsv(excluded, originalRows, headers, t)
  }

  // ── 네비게이션 ──

  const handleNext = () => {
    if (phase === 'upload') {
      if (preset.id === 'googleauth') {
        handleGoogleAuthNext()
        return
      }
      parseFileAndAdvance()
      return
    }
    if (stepLabel === '컬럼 매핑') {
      handleMappingNext()
      return
    }
    dispatch({ type: 'GO_NEXT' })
  }

  const handlePrev = () => {
    dispatch({ type: 'GO_PREV' })
  }

  // ── 모달 닫기 ──

  // RESET 전에 번호표를 바꾼다 — RESET 과 닫힘 effect 사이에 끝난 작업도 버리기 위해
  const closeAndReset = () => {
    parseGenerationRef.current += 1
    decryptModalRef.current?.destroy()
    decryptModalRef.current = null
    onClose()
    dispatch({ type: 'RESET' })
  }

  const handleClose = () => {
    // 처리(파일 읽기·복호화·QR 확인) 중에는 닫지 않는다 — 늦게 끝난 작업이 새 세션 위에 결과를 쏟는다
    if (isParsing) return
    if (isImporting) {
      Modal.confirm({
        title: t('record.error.importInProgress'),
        onOk: closeAndReset,
      })
      return
    }
    closeAndReset()
  }

  // ── 다음 버튼 비활성화 조건 ──

  const isNextDisabled = () => {
    if (phase === 'upload') {
      if (isParsing) return true
      if (preset.id === 'googleauth') return isQrScanning || scannedQrs.length === 0
      if (!fileName) return true
      // 템플릿 스키마 로드가 성공 상태가 아니면 차단 (officepassword 제외)
      if (!isTemplateReady) return true
    }
    // 중복 연결·필수 필드 미연결·사용자 정의 필드 이름 편집 중(✓ 미확정)이면 막는다 — 푸터의 안내 문구와 같은 판정이다
    if (phase === 'steps' && stepLabel === '컬럼 매핑') {
      if (getMappingBlockReason(mappings)) return true
    }
    return false
  }

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={700}
      closable={false}
      maskClosable={!isImporting && !isParsing}
      keyboard={!isParsing}
      wrapClassName={styles.importV2Modal}
      destroyOnClose
    >
      <div className={styles.modalContainer}>
        {/* 헤더 */}
        <div className={styles.modalHeader}>
          <div className={styles.headerTop}>
            <h2 className={styles.headerTitle}>{t('record.import.v2.title')}</h2>
            <StepIndicator indicator={getStepIndicator({ phase, step, preset })} t={t} />
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleClose}
              disabled={isParsing}
            >
              &times;
            </button>
          </div>
        </div>

        {/* 바디 */}
        <div className={styles.modalBody}>
          {phase === 'upload' && (
            <ImportUploadStep
              preset={preset}
              fileName={fileName}
              dragging={false}
              dispatch={dispatch}
              templateSchemas={templateSchemas}
              onRetryTemplate={loadTemplateForPreset}
              scannedQrs={scannedQrs}
              isParsing={isParsing}
              onQrScanningChange={setIsQrScanning}
              templateCatalog={templateCatalog}
              selectedTemplateId={selectedTemplateId}
              templateDetection={templateDetection}
              onRetryCatalog={loadTemplateCatalog}
              t={t}
            />
          )}
          {phase === 'steps' && stepLabel === '컬럼 매핑' && (
            <ImportMappingStep
              preset={preset}
              mappings={mappings}
              csvColumns={csvColumns}
              savedAsTemplateName={savedAsTemplate?.displayName ?? null}
              translations={
                isTemplatePreset
                  ? selectedTemplate?.schema?.translations
                  : currentTemplateState?.schema?.translations
              }
              dispatch={dispatch}
              t={t}
            />
          )}
          {phase === 'steps' && stepLabel === '유효성 검증' && !shouldAutoImport && (
            <ImportValidationStep errors={validationErrors} dispatch={dispatch} t={t} />
          )}
          {phase === 'steps' && stepLabel === 'Import 완료' && (
            <ImportCompleteStep
              importResult={importResult}
              onDownloadExcluded={handleDownloadExcluded}
              t={t}
            />
          )}
        </div>

        {/* 푸터 (완료 스텝은 자체 푸터 — "확인" 1개 버튼) */}
        {stepLabel === 'Import 완료' && (
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleConfirm}
              disabled={isFinalizing}
            >
              {t('record.import.v2.complete.confirm')}
            </button>
          </div>
        )}
        {stepLabel !== 'Import 완료' && !shouldAutoImport && (
          <div
            className={classNames(styles.modalFooter, phase === 'steps' && styles.footerBetween)}
          >
            {phase === 'steps' && (
              <button type="button" className={styles.btnSecondary} onClick={handlePrev}>
                {t('record.import.v2.prev')}
              </button>
            )}
            {stepLabel === '컬럼 매핑' && (
              <MappingBlockNotice reason={getMappingBlockReason(mappings)} t={t} />
            )}
            {stepLabel === '유효성 검증' ? (
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={pendingCount > 0 || importCnt === 0 || isImporting}
                onClick={handleImportExecute}
              >
                {t('record.import.v2.validation.runImport', { count: importCnt })}
              </button>
            ) : (
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={isNextDisabled()}
                onClick={handleNext}
              >
                {t('record.import.v2.next')}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

/**
 * 암호화된 JSON 복호화 처리 (서브 팝업)
 *
 * v2.6 변경:
 * - messageApi → errorBase 팝업 전환
 */
const handleEncryptedJson = async (encryptedData, t, messageApi, dispatch, opts = {}) => {
  // opts 는 비밀번호 재입력 때 그대로 다시 넘긴다
  const { preset, typeList = [], isStale = () => false, decryptModalRef } = opts
  const passwordRef = { value: '' }
  const modalRef = { current: null }
  // 팝업마다의 상태 — [취소] 는 즉시 기록되고, 제출은 한 번만 받는다
  let isCancelled = false
  let isSubmitted = false

  const rejectKeepOpen = () => {
    const p = Promise.reject()
    p.catch(() => {})
    return p
  }

  // 복호화는 비밀번호 팝업이 아니라 진행률 창에서 한다 — 저장 단계 진행률 창(handleImportExecute)과 같은 모양으로
  // 닫기·Esc·바깥 클릭·버튼을 모두 막아, 처리 중에 창이 닫혀 늦은 결과가 새 세션에 들어가는 것을 막는다
  const runDecryptWithProgress = async (flatRecords, password) => {
    const progressModal = Modal.confirm({
      title: t('record.result.decryptLoading'),
      icon: null,
      width: 480,
      content: makeProgressContent(0, flatRecords.length, t),
      closable: false,
      keyboard: false,
      maskClosable: false,
      okButtonProps: { style: { display: 'none' } },
      cancelButtonProps: { style: { display: 'none' } },
      wrapClassName: classNames('om-popup-1'),
    })
    dispatch({ type: 'SET_PARSING', payload: true })
    try {
      const decrypted = await decryptRecords(flatRecords, password, (done, total) => {
        progressModal.update({ content: makeProgressContent(done, total, t) })
      })
      progressModal.destroy()
      if (isStale()) return

      let records = decrypted
      if (!Array.isArray(records)) records = [records]

      dispatch({ type: 'SET_PARSED_DATA', payload: { parsedData: records } })

      // 유효성 검증 결과를 dispatch하고 validation 화면으로 이동 (에러 유무 무관)
      const validation = validateRecords(records, preset || { id: 'officepassword' }, [], typeList)
      dispatch({
        type: 'SET_VALIDATION_RESULT',
        payload: {
          errors: validation.errors,
          validRecords: validation.validRecords,
          totalRows: records.length,
        },
      })

      dispatch({ type: 'GO_NEXT' })
    } catch (error) {
      progressModal.destroy()
      if (isStale()) return
      // 오류 창이 닫힌 뒤(afterClose — 확인·Esc 모두 닫힘 애니메이션이 끝난 뒤 1회, antd 5.21.6 es/modal/confirm.js:93-106)
      // 비밀번호 팝업을 다시 띄운다. 동시에 띄우면 팝업의 Enter 처리기(querySelector 는 DOM 순서상 첫 확인 버튼을
      // 고른다)가 먼저 만들어진 오류 창 버튼을 눌러 제출되지 않는다. onOk 는 쓰지 않는다 — 닫기는 onOk 의 Promise 가
      // 끝난 뒤라 닫히기 전에 불린다(es/_util/ActionButton.js:48-51)
      const reopen = () => {
        if (!isStale()) handleEncryptedJson(encryptedData, t, messageApi, dispatch, opts)
      }
      const isChecksumError =
        error?.message === IMPORT_ERROR.CHECKSUM_MISMATCH ||
        error?.message === IMPORT_ERROR.CHECKSUM_MISSING
      errorBase({
        title: t('record.import.v2.error.title'),
        content: t(
          isChecksumError ? 'record.error.corruptedImportFile' : 'record.error.decryptFailed',
        ),
        afterClose: reopen,
      })
    } finally {
      if (!isStale()) dispatch({ type: 'SET_PARSING', payload: false })
    }
  }

  const handleOk = async () => {
    // 팝업이 닫히는 동안(애니메이션) Enter 처리기가 같은 확인 버튼을 다시 누를 수 있다 — antd 는 onOk 가 resolve 된
    // 직후 버튼 잠금을 풀기 때문에(es/_util/ActionButton.js:48-51) 두 번째 실행을 여기서 막는다
    if (isSubmitted) return undefined
    if (!passwordRef.value) {
      errorBase({
        title: t('record.import.v2.error.title'),
        content: t('record.error.decryptFailed'),
      })
      return rejectKeepOpen()
    }

    try {
      const { records: encRecords, checksum } = encryptedData
      const flatRecords = encRecords.map((r) => (Array.isArray(r) ? r.join('') : r))

      if (checksum) {
        const expected = await computeSHA256Hex(flatRecords.join(''))
        if (isCancelled || isStale()) return undefined
        if (expected !== checksum) {
          errorBase({
            title: t('record.import.v2.error.title'),
            content: t('record.error.corruptedImportFile'),
          })
          return rejectKeepOpen()
        }
      }
      if (isCancelled || isStale()) return undefined

      // await 하지 않는다 — 이 resolve 로 antd 가 비밀번호 팝업을 한 번 닫고, 복호화는 진행률 창에서 이어진다
      isSubmitted = true
      runDecryptWithProgress(flatRecords, passwordRef.value)
      return undefined
    } catch (error) {
      if (isCancelled || isStale()) return undefined
      // 체크섬 계산(대용량 join·digest) 실패 — 팝업을 유지하고 지금처럼 알린다
      errorBase({
        title: t('record.import.v2.error.title'),
        content: t('record.error.decryptFailed'),
      })
      return rejectKeepOpen()
    }
  }

  // bulkImport 의 showDecryptModal 과 동일한 시각 규격 — confirmBase(om-popup-1) + 공용 decrypt 스타일
  modalRef.current = confirmBase({
    wrapClassName: classNames(decryptStyles.decryptModal),
    icon: <img src={exclamationBlackIcon} alt="info" width={22} height={22} />,
    title: <div className={decryptStyles.decryptTitle}>{t('record.desc.decryptFileTitle')}</div>,
    width: 480,
    content: (
      <div className={decryptStyles.decryptModal}>
        <div className={decryptStyles.description}>{t('record.desc.decryptFileDesc')}</div>
        <div className={decryptStyles.passwordField}>
          <PasswordInput
            onChange={(e) => {
              passwordRef.value = e.target.value
            }}
            onPressEnter={() => {
              const okBtn = document.querySelector('.ant-modal-confirm-btns .ant-btn-primary')
              if (okBtn) okBtn.click()
            }}
            placeholder={t('record.desc.decryptFilePlaceholder')}
          />
        </div>
      </div>
    ),
    okText: t('common.button.ok'),
    cancelText: t('common.button.cancel'),
    okButtonProps: { className: classNames('type7', 'w100', 'h38') },
    cancelButtonProps: { className: classNames('w100', 'h38') },
    onOk: handleOk,
    // 출구는 누르는 즉시 기록되는 [취소] 버튼만 둔다(confirmBase 가 이 값으로 Esc 도 끈다 — utils/ui/index.js:829-830).
    // Esc·바깥 클릭으로 닫힌 뒤 [확인] 처리가 끝나면 antd 5.21.6 이 close() 를 인자 없이 다시 불러(es/_util/ActionButton.js:50)
    // 예약된 afterClose 와 취소 표시(triggerCancel)가 덮어써져 onCancel 이 불리지 않는다(es/modal/confirm.js:60-62·93-106)
    // — 그러면 취소했는데 복호화·자동 저장이 이어진다
    maskClosable: false,
    onCancel: () => {
      isCancelled = true
    },
  })
  // 가져오기 창을 닫을 때 남은 팝업을 닫을 수 있게 핸들을 넘긴다(정적 모달은 따로 렌더된다 — es/modal/confirm.js:49·86)
  if (decryptModalRef) decryptModalRef.current = modalRef.current
}

export default ImportV2Modal
